import classNames from 'classnames';
import React from 'react';
import { Table } from 'react-bootstrap';
import { useFilters, usePagination, useTable, useSortBy, useResizeColumns, useFlexLayout } from 'react-table';
import styles from './style.module.scss';
import { useSelector } from 'react-redux';
import { NextIcon, PrevIcon } from 'assets';
import NotRecord from 'shared/components/not_record/NotRecord';

const GenericTable = ({ 
    data, 
    tableName, 
    OffPage, 
    TableWrapperStyle, 
    ColumnStyleIndex, 
    TDStyle, 
    TDTextStyle, 
    FirstTableTD, 
    ColumnStyle, 
    CoumnHeadingStyle, 
    columns = [], 
    loading, 
    showPagination = true, 
    showColumnCustomizer = true,
    onRowClick, 
    renderRowSubComponent, 
    ...rest 
}) => {
    const {
        theme: { theme },
        user: { user }
    } = useSelector((state) => state.root);

    // User & Table specific persistence keys
    const userId = user?._id || user?.id || user?.email || 'default_user';
    const identifier = tableName || window.location.pathname;
    const storageKey = `table_pagination_${identifier}`;
    const colPrefKey = `user_col_pref_${userId}_${identifier}`;

    // Helper functions for column identifiers and labels
    const getColId = React.useCallback((col, idx) => {
        return col.id || (typeof col.accessor === 'string' ? col.accessor : null) || (typeof col.Header === 'string' ? col.Header : `col_${idx}`);
    }, []);

    const getColTitle = React.useCallback((col, idx) => {
        if (typeof col.Header === 'string') return col.Header;
        if (col.Header && React.isValidElement(col.Header)) {
            try {
                const findText = (node) => {
                    if (!node) return '';
                    if (typeof node === 'string' || typeof node === 'number') return String(node);
                    if (Array.isArray(node)) return node.map(findText).filter(Boolean).join(' ');
                    if (node.props?.children) return findText(node.props.children);
                    return '';
                };
                const text = findText(col.Header).trim();
                if (text) return text;
            } catch (e) {}
        }
        if (col.title) return col.title;
        if (typeof col.accessor === 'string') {
            return col.accessor.charAt(0).toUpperCase() + col.accessor.slice(1);
        }
        return col.id ? String(col.id) : `Column ${idx + 1}`;
    }, []);

    // State for column visibility and order
    const [columnConfig, setColumnConfig] = React.useState(() => {
        try {
            const saved = localStorage.getItem(colPrefKey);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    const currentIds = (columns || []).map((col, idx) => getColId(col, idx));
                    const savedIds = new Set(parsed.map(p => p.id));
                    
                    const reconciled = parsed.filter(p => currentIds.includes(p.id));
                    // Only apply if saved preferences match the majority of current table columns
                    if (reconciled.length >= Math.ceil(currentIds.length / 2)) {
                        currentIds.forEach(id => {
                            if (!savedIds.has(id)) {
                                reconciled.push({ id, visible: true });
                            }
                        });
                        return reconciled;
                    }
                }
            }
        } catch (e) {
            console.error("Error loading column preferences:", e);
        }
        return (columns || []).map((col, idx) => ({ id: getColId(col, idx), visible: true }));
    });

    // Reload preferences when colPrefKey changes (e.g. tableName changes)
    React.useEffect(() => {
        try {
            const saved = localStorage.getItem(colPrefKey);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    const currentIds = (columns || []).map((col, idx) => getColId(col, idx));
                    const savedIds = new Set(parsed.map(p => p.id));
                    
                    const reconciled = parsed.filter(p => currentIds.includes(p.id));
                    if (reconciled.length >= Math.ceil(currentIds.length / 2)) {
                        currentIds.forEach(id => {
                            if (!savedIds.has(id)) {
                                reconciled.push({ id, visible: true });
                            }
                        });
                        setColumnConfig(reconciled);
                        return;
                    }
                }
            }
        } catch (e) {
            console.error("Error reloading column preferences:", e);
        }
        setColumnConfig((columns || []).map((col, idx) => ({ id: getColId(col, idx), visible: true })));
    }, [colPrefKey]);

    // Reconcile when columns prop dynamically changes
    React.useEffect(() => {
        setColumnConfig(prev => {
            const currentIds = (columns || []).map((col, idx) => getColId(col, idx));
            const prevIds = new Set(prev.map(p => p.id));
            
            // Check how many of current columns were in previous state
            const matchCount = currentIds.filter(id => prevIds.has(id)).length;
            if (matchCount < Math.ceil(currentIds.length / 2)) {
                // Different table columns: preserve exact column order from prop
                return (columns || []).map((col, idx) => ({ id: getColId(col, idx), visible: true }));
            }

            let updated = prev.filter(p => currentIds.includes(p.id));
            currentIds.forEach(id => {
                if (!prevIds.has(id)) {
                    updated.push({ id, visible: true });
                }
            });
            return updated;
        });
    }, [columns, getColId]);

    // Save preferences
    const saveColumnConfig = (newConfig) => {
        setColumnConfig(newConfig);
        try {
            localStorage.setItem(colPrefKey, JSON.stringify(newConfig));
        } catch (e) {
            console.error("Error saving column preferences:", e);
        }
    };

    const toggleColumn = (id) => {
        const newConfig = columnConfig.map(c => c.id === id ? { ...c, visible: !c.visible } : c);
        // Ensure at least one column remains visible
        if (newConfig.some(c => c.visible)) {
            saveColumnConfig(newConfig);
        }
    };

    const showAllColumns = () => {
        const newConfig = columnConfig.map(c => ({ ...c, visible: true }));
        saveColumnConfig(newConfig);
    };

    const resetColumns = () => {
        try {
            localStorage.removeItem(colPrefKey);
        } catch (e) {}
        const defaultConfig = (columns || []).map((col, idx) => ({ id: getColId(col, idx), visible: true }));
        setColumnConfig(defaultConfig);
    };

    const moveColumn = (index, direction, e) => {
        if (e) e.stopPropagation();
        const newConfig = [...columnConfig];
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= newConfig.length) return;
        const temp = newConfig[index];
        newConfig[index] = newConfig[targetIndex];
        newConfig[targetIndex] = temp;
        saveColumnConfig(newConfig);
    };

    // Drag-and-drop state
    const [draggedIndex, setDraggedIndex] = React.useState(null);

    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (e, targetIndex) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === targetIndex) return;
        const newConfig = [...columnConfig];
        const [movedItem] = newConfig.splice(draggedIndex, 1);
        newConfig.splice(targetIndex, 0, movedItem);
        setDraggedIndex(null);
        saveColumnConfig(newConfig);
    };

    // UI state for customizer panel
    const [showCustomizer, setShowCustomizer] = React.useState(false);

    // Compute active columns for react-table
    const activeColumns = React.useMemo(() => {
        const colMap = new Map((columns || []).map((col, idx) => [getColId(col, idx), col]));
        const result = [];
        
        columnConfig.forEach(cfg => {
            if (cfg.visible && colMap.has(cfg.id)) {
                result.push(colMap.get(cfg.id));
            }
        });

        return result.length > 0 ? result : (columns || []);
    }, [columns, columnConfig, getColId]);

    // Load initial pagination from sessionStorage
    const initialPageIndex = React.useMemo(() => {
        const stored = sessionStorage.getItem(`${storageKey}_pageIndex`);
        return stored !== null ? Number(stored) : 0;
    }, [storageKey]);

    const initialPageSize = React.useMemo(() => {
        const stored = sessionStorage.getItem(`${storageKey}_pageSize`);
        return stored !== null ? Number(stored) : 10;
    }, [storageKey]);

    const defaultColumn = React.useMemo(() => ({
        minWidth: 80,
        width: 150,
        maxWidth: 2000,
    }), []);

    const tableInstance = useTable(
        { 
            columns: activeColumns, 
            data, 
            defaultColumn, 
            initialState: { 
                pageIndex: initialPageIndex,
                pageSize: initialPageSize
            },
            autoResetResizing: false,
            autoResetSortBy: false,
            autoResetFilters: false,
            autoResetPage: false,
            ...rest
        },
        useFilters,
        useSortBy,
        useFlexLayout,
        useResizeColumns,
        usePagination
    );
    const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page, state, nextPage, previousPage, canNextPage, canPreviousPage, pageCount, gotoPage, setPageSize } = tableInstance;

    // Update sessionStorage whenever pageIndex or pageSize changes
    React.useEffect(() => {
        sessionStorage.setItem(`${storageKey}_pageIndex`, state.pageIndex);
    }, [state.pageIndex, storageKey]);

    React.useEffect(() => {
        sessionStorage.setItem(`${storageKey}_pageSize`, state.pageSize);
    }, [state.pageSize, storageKey]);

    const pageNumbers = [];
    const currentPage = state.pageIndex;
    const totalPages = pageCount;

    const rangeStart = Math.max(currentPage - 1, 0);
    const rangeEnd = Math.min(currentPage + 2, totalPages - 1);

    for (let i = rangeStart; i <= rangeEnd; i++) {
        pageNumbers.push(i);
    }

    const activeCount = columnConfig.filter(c => c.visible).length;
    const totalCount = columnConfig.length;

    // Map column ID to title lookup
    const colTitleMap = React.useMemo(() => {
        const map = {};
        (columns || []).forEach((col, idx) => {
            const id = getColId(col, idx);
            map[id] = getColTitle(col, idx);
        });
        return map;
    }, [columns, getColId, getColTitle]);

    return (
        <div className={classNames(styles.TableWrapper, TableWrapperStyle && TableWrapperStyle)}>
            {/* Column Customizer Toolbar */}
            {showColumnCustomizer && columns && columns.length > 0 && (
                <div className={styles.ColumnCustomizerWrapper}>
                    <div className={styles.CustomizerHeader}>
                        <button 
                            type="button" 
                            className={classNames(styles.CustomizeBtn, showCustomizer && styles.ActiveBtn)}
                            onClick={() => setShowCustomizer(!showCustomizer)}
                            title="Customize Visible Columns & Order"
                        >
                            <span>⚙️</span>
                            <span>Columns ({activeCount}/{totalCount})</span>
                            <span style={{ fontSize: '10px' }}>{showCustomizer ? '▲' : '▼'}</span>
                        </button>
                    </div>

                    {showCustomizer && (
                        <div className={styles.CustomizerPanel}>
                            <div className={styles.PanelTop}>
                                <span>🖱️ Drag to reorder • Click column badge to show/hide</span>
                                <div className={styles.PanelActions}>
                                    <span className={styles.ActionLink} onClick={showAllColumns}>Show All</span>
                                    <span>•</span>
                                    <span className={styles.ActionLink} onClick={resetColumns}>Reset Default</span>
                                </div>
                            </div>

                            <div className={styles.ChipsContainer}>
                                {columnConfig.map((cfg, idx) => {
                                    const title = colTitleMap[cfg.id] || cfg.id;
                                    const isActive = cfg.visible;

                                    return (
                                        <div
                                            key={cfg.id}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, idx)}
                                            onDragOver={handleDragOver}
                                            onDrop={(e) => handleDrop(e, idx)}
                                            onClick={() => toggleColumn(cfg.id)}
                                            className={classNames(
                                                styles.ColumnChip,
                                                isActive ? styles.ActiveChip : styles.InactiveChip,
                                                draggedIndex === idx && styles.Dragging
                                            )}
                                            title={isActive ? `Click to hide '${title}' • Drag to reorder` : `Click to show '${title}'`}
                                        >
                                            <span className={styles.DragIcon} title="Drag to reorder">⋮⋮</span>
                                            <span>{isActive ? '✓' : '+'}</span>
                                            <span className={styles.ChipTitle}>{title}</span>
                                            {isActive && (
                                                <div className={styles.MoveArrows}>
                                                    {idx > 0 && (
                                                        <span 
                                                            title="Move Left" 
                                                            onClick={(e) => moveColumn(idx, -1, e)}
                                                        >
                                                            ◀
                                                        </span>
                                                    )}
                                                    {idx < columnConfig.length - 1 && (
                                                        <span 
                                                            title="Move Right" 
                                                            onClick={(e) => moveColumn(idx, 1, e)}
                                                        >
                                                            ▶
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className={styles.TableContainer}>
                <Table {...getTableProps()} className={classNames(styles.Table)}>
                    <thead className={classNames(styles.TableHead)}>
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()} className={classNames('d-flex', styles.TableRow)}>
                                {headerGroup.headers.map((column, columnInx) => {
                                    const headerProps = column.getHeaderProps();
                                    const sortByProps = column.getSortByToggleProps();

                                    return (
                                        <th
                                            {...headerProps}
                                            className={classNames(
                                                `${theme}FontColor`,
                                                `${theme}TableBg`,
                                                columnInx === ColumnStyleIndex - 1 ? CoumnHeadingStyle : styles.HeaderContainer,
                                                columnInx === 0 && FirstTableTD && FirstTableTD
                                            )}
                                            style={{
                                                ...headerProps.style,
                                                position: 'sticky',
                                                top: 0,
                                                zIndex: 12
                                            }}
                                        >
                                            <div
                                                className="d-flex align-items-center w-100"
                                                onClick={column.canSort ? sortByProps.onClick : undefined}
                                                style={{ cursor: column.canSort ? 'pointer' : 'default', whiteSpace: 'nowrap' }}
                                            >
                                                {column.render('Header')}
                                                {column.canSort && (
                                                    <span className="ms-1" style={{ fontSize: '10px' }}>
                                                        {column.isSorted
                                                            ? column.isSortedDesc
                                                                ? ' 🔽'
                                                                : ' 🔼'
                                                            : ''}
                                                    </span>
                                                )}
                                            </div>
                                            
                                            {/* RESIZE HANDLE */}
                                            <div
                                                {...column.getResizerProps()}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                }}
                                                className={classNames(
                                                    styles.resizer,
                                                    column.isResizing && styles.isResizing
                                                )}
                                            />
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    {loading ? (
                        <>Loading...</>
                    ) : data?.length > 0 ? (
                        <tbody {...getTableBodyProps()} className={classNames(styles.TableBody)}>
                            {page.map((row) => {
                                prepareRow(row);
                                const rowProps = row.getRowProps();
                                return (
                                    <React.Fragment key={row.id || row.original?._id}>
                                        <tr
                                            {...rowProps}
                                            className={classNames(
                                                'd-flex',
                                                styles.TableRow,
                                                'mb-2',
                                                row.original?.closed === "true" && styles.RowClosed,
                                                row.original?.closed === "false" && styles.RowNotClosed,
                                                row.original?.isChildRow && styles.RowChild
                                            )}
                                            onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                                            style={{
                                                ...rowProps.style,
                                                ...(onRowClick ? { cursor: 'pointer' } : {})
                                            }}
                                        >
                                            {row.cells.map((cell, inx) => (
                                                <td
                                                    {...cell.getCellProps()}
                                                    className={classNames(
                                                        `${theme}TableBg`,
                                                        inx == ColumnStyleIndex - 1 ? ColumnStyle : TDStyle ? TDStyle : styles.TableTD,
                                                        inx == 0 && FirstTableTD && FirstTableTD
                                                    )}
                                                >
                                                    <div className={classNames(`${theme}FontBlackTwo`, TDTextStyle ? TDTextStyle : styles.Text)}>
                                                        {cell.render('Cell')}
                                                    </div>
                                                </td>
                                            ))}
                                        </tr>
                                        {renderRowSubComponent && renderRowSubComponent({ row })}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    ) : (
                        <NotRecord />
                    )}
                </Table>
            </div>
            {data?.length > 0 && showPagination && (
                <div className={styles.Pagination}>
                    <div className="d-flex align-items-center justify-content-between w-100 px-3 mt-2">
                        <div className="d-flex align-items-center">
                            <span className="me-2" style={{ fontSize: '13px', color: '#6c757d', fontWeight: '500' }}>Items per page:</span>
                            <select
                                value={state.pageSize}
                                onChange={e => {
                                    setPageSize(Number(e.target.value));
                                }}
                                className="form-select form-select-sm shadow-none"
                                style={{ width: '75px', cursor: 'pointer', fontSize: '13px' }}
                            >
                                {[10, 20, 50, 100].map(pageSize => (
                                    <option key={pageSize} value={pageSize}>
                                        {pageSize}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className={styles.PaginationContainer}>
                            <button onClick={previousPage} disabled={!canPreviousPage} className={state.pageIndex === 0 ? styles.DisableButton : ''}>
                                <PrevIcon style={{ height: '22px', width: '22px', marginTop: '2px' }} />
                                <div>prev</div>
                            </button>
                            {pageNumbers.map((pageIndex) => (
                                <button key={pageIndex} onClick={() => gotoPage(pageIndex)} className={pageIndex === state.pageIndex ? 'active' : ''}>
                                    {pageIndex === state.pageIndex ? <span className={styles.ActivePage}>{pageIndex + 1}</span> : <div>{pageIndex + 1}</div>}
                                </button>
                            ))}
                            <button onClick={nextPage} disabled={!canNextPage} className={state.pageIndex === totalPages - 1 ? styles.DisableButton : ''}>
                                <div>next</div>
                                <NextIcon style={{ height: '22px', width: '22px', marginTop: '2px' }} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GenericTable;
